import React, { useState, useMemo } from 'react';
import { Plus, Edit, Trash2, Download, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { toast } from '../../utils/toast';

interface TravelAllowance {
  id: string;
  description: string;
  amount: string;
}

interface OtherAllowance {
  role: string;
  da_min: number;
  da_max: number;
  ex_min: number;
  ex_max: number;
  out_min: number;
  out_max: number;
}

interface FareRoute {
  id: string;
  route_id: string;
  route_name: string;
  cities: string;
  zone: string;
  division: string;
  route_for: string;
  designation: string;
  mode: string;
  distance: number;
  fare: number;
}

const AllowanceSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'fare' | 'travel' | 'other'>('fare');
  const [travelAllowances, setTravelAllowances] = useState<TravelAllowance[]>([
    { id: '1', description: 'Travel 50 - 150', amount: '50 - 150' }
  ]);
  const [showTravelForm, setShowTravelForm] = useState(false);
  const [newTravel, setNewTravel] = useState({ description: '', amount: '' });

  const [otherAllowances] = useState<OtherAllowance[]>([
    { role: 'ZSM', da_min: 500, da_max: 500, ex_min: 500, ex_max: 500, out_min: 0, out_max: 0 },
    { role: 'RBM', da_min: 0, da_max: 0, ex_min: 0, ex_max: 0, out_min: 0, out_max: 0 },
    { role: 'RBM', da_min: 350, da_max: 350, ex_min: 350, ex_max: 350, out_min: 500, out_max: 1200 },
    { role: 'ASM', da_min: 300, da_max: 300, ex_min: 300, ex_max: 300, out_min: 200, out_max: 800 },
    { role: 'MR', da_min: 220, da_max: 220, ex_min: 220, ex_max: 220, out_min: 200, out_max: 600 }
  ]);

  const [fareRoutes, setFareRoutes] = useState<FareRoute[]>([
    { id: '1', route_id: '1026', route_name: 'Ara To Behea', cities: 'Behea', zone: 'Bihar', division: 'Main', route_for: 'Chandra Shekhar Acharya', designation: 'All', mode: 'Bike', distance: 80, fare: 200 },
    { id: '2', route_id: '1634D', route_name: 'Ara To Behea', cities: 'Behea', zone: 'Bihar', division: 'Main', route_for: 'ACHALENDRA KUMAR SINHA', designation: 'All', mode: 'Bike', distance: 80, fare: 220 },
    { id: '3', route_id: '945', route_name: 'Ara To Behea', cities: 'Behea', zone: 'Bihar', division: 'Main', route_for: 'MANISH MISHRA', designation: 'All', mode: 'Bike', distance: 80, fare: 200 },
    { id: '4', route_id: '1180', route_name: 'Ara To Behea', cities: 'Behea', zone: 'Bihar', division: 'Main', route_for: '', designation: 'All', mode: 'Bike', distance: 80, fare: 220 },
    { id: '5', route_id: '1179', route_name: 'Ara To Baniwaliya', cities: 'Dhobaha.Balua', zone: 'Bihar', division: 'Main', route_for: '', designation: 'All', mode: 'Bike', distance: 80, fare: 220 },
    { id: '6', route_id: '1181', route_name: 'Ara To Chandi', cities: 'Akhiganj', zone: 'Bihar', division: 'Main', route_for: '', designation: 'All', mode: 'Bike', distance: 90, fare: 247.5 },
    { id: '7', route_id: '1178', route_name: 'Ara To Jagdishpur', cities: 'Sonharsha', zone: 'Bihar', division: 'Main', route_for: '', designation: 'All', mode: 'Bike', distance: 110, fare: 302.5 },
    { id: '8', route_id: '1182', route_name: 'Ara To Patna', cities: 'Patna', zone: 'Bihar', division: 'Main', route_for: '', designation: 'All', mode: 'Train', distance: 70, fare: 192.5 },
    { id: '9', route_id: '1177', route_name: 'Ara To Piro', cities: 'Gadahni', zone: 'Bihar', division: 'Main', route_for: '', designation: 'All', mode: 'Bike', distance: 110, fare: 302.5 },
    { id: '10', route_id: '1176', route_name: 'Ara To shahpur', cities: 'Barahai', zone: 'Bihar', division: 'Main', route_for: '', designation: 'All', mode: 'Bike', distance: 100, fare: 275 }
  ]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [filters, setFilters] = useState({ mode: '', main: '', zone: '', search: '' });
  const [showRouteForm, setShowRouteForm] = useState(false);
  const [editingRoute, setEditingRoute] = useState<FareRoute | null>(null);
  const [routeFormData, setRouteFormData] = useState<Partial<FareRoute>>({
    route_id: '', route_name: '', cities: '', zone: '', division: 'Main',
    route_for: '', designation: 'All', mode: 'Bike', distance: 0, fare: 0
  });

  const handleAddTravel = () => {
    if (newTravel.description && newTravel.amount) {
      setTravelAllowances([...travelAllowances, { id: Date.now().toString(), ...newTravel }]);
      setNewTravel({ description: '', amount: '' });
      setShowTravelForm(false);
    }
  };

  const handleDeleteTravel = async (id: string) => {
    if (await toast.confirm('This action cannot be undone.', 'Delete travel allowance?', 'Delete')) {
      setTravelAllowances(travelAllowances.filter(t => t.id !== id));
    }
  };

  const handleDownload = () => {
    const csv = [
      ['Route ID', 'Route Name', 'Cities', 'Zone', 'Division', 'Route For', 'Designation', 'Mode', 'Distance', 'Fare'],
      ...filteredRoutes.map(route => [
        route.route_id, route.route_name, route.cities, route.zone, route.division,
        route.route_for, route.designation, route.mode, route.distance, route.fare
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'fare-routes.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleAddRoute = () => {
    setEditingRoute(null);
    setRouteFormData({
      route_id: '', route_name: '', cities: '', zone: 'Bihar', division: 'Main',
      route_for: '', designation: 'All', mode: 'Bike', distance: 0, fare: 0
    });
    setShowRouteForm(true);
  };

  const handleEditRoute = (route: FareRoute) => {
    setEditingRoute(route);
    setRouteFormData(route);
    setShowRouteForm(true);
  };

  const handleSaveRoute = () => {
    if (!routeFormData.route_id || !routeFormData.route_name || !routeFormData.cities) {
      toast.warning('Please fill in all required fields.');
      return;
    }

    if (editingRoute) {
      setFareRoutes(fareRoutes.map(r => r.id === editingRoute.id ? { ...routeFormData, id: editingRoute.id } as FareRoute : r));
    } else {
      setFareRoutes([...fareRoutes, { ...routeFormData, id: Date.now().toString() } as FareRoute]);
    }

    setShowRouteForm(false);
    setEditingRoute(null);
  };

  const filteredRoutes = useMemo(() => {
    return fareRoutes.filter(route => {
      const matchesMode = !filters.mode || route.mode === filters.mode;
      const matchesZone = !filters.zone || route.zone === filters.zone;
      const matchesSearch = !filters.search ||
        route.route_name.toLowerCase().includes(filters.search.toLowerCase()) ||
        route.cities.toLowerCase().includes(filters.search.toLowerCase()) ||
        route.route_for.toLowerCase().includes(filters.search.toLowerCase()) ||
        route.route_id.toLowerCase().includes(filters.search.toLowerCase());
      return matchesMode && matchesZone && matchesSearch;
    });
  }, [fareRoutes, filters]);

  const totalPages = Math.ceil(filteredRoutes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRoutes = filteredRoutes.slice(startIndex, endIndex);

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  return (
    <div className="bg-white rounded-xl shadow-md">
      <div className="mb-6">
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
          {['fare', 'travel', 'other'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as 'fare' | 'travel' | 'other')}
              className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'fare' ? 'Standard Fare Chart' : tab === 'travel' ? 'Travel Allowance' : 'Other Allowances'}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">
        {activeTab === 'fare' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Standard Fare Chart</h2>
              <div className="flex gap-2">
                <button onClick={handleAddRoute} className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />Add
                </button>
                <button onClick={handleDownload} className="flex items-center px-4 py-2 border border-blue-600 text-blue-600 rounded hover:bg-blue-50">
                  <Download className="w-4 h-4 mr-2" />Download
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4 mb-6">
              <select value={filters.mode} onChange={(e) => setFilters({ ...filters, mode: e.target.value })} className="px-3 py-2 border rounded-lg lg:col-span-2">
                <option value="">All Modes</option>
                <option value="Bike">Bike</option>
                <option value="Train">Train</option>
              </select>
              <select value={filters.main} onChange={(e) => setFilters({ ...filters, main: e.target.value })} className="px-3 py-2 border rounded-lg lg:col-span-2">
                <option value="">Main</option>
              </select>
              <select value={filters.zone} onChange={(e) => setFilters({ ...filters, zone: e.target.value })} className="px-3 py-2 border rounded-lg lg:col-span-2">
                <option value="">All Zone</option>
                <option value="Bihar">Bihar</option>
              </select>
              <div className="flex gap-2 md:col-span-2 lg:col-span-1">
                <input type="text" placeholder="Search..." value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="flex-1 px-3 py-2 border rounded-lg" />
                <button onClick={() => setCurrentPage(1)} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 whitespace-nowrap">Go</button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {['Action', 'Route Id', 'Route Name', 'Cities', 'Zone', 'Division', 'Route For', 'Designation', 'Mode', 'Distance', 'Fare'].map(header => (
                      <th key={header} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentRoutes.map((route) => (
                    <tr key={route.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button onClick={() => handleEditRoute(route)} className="text-blue-600 hover:text-blue-900">
                          <Edit className="w-4 h-4" />
                        </button>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">{route.route_id}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">{route.route_name}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">{route.cities}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">{route.zone}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">{route.division}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">{route.route_for || '-'}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">{route.designation}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">{route.mode}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">{route.distance}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">{route.fare}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-gray-600">Showing {startIndex + 1} to {Math.min(endIndex, filteredRoutes.length)} of {filteredRoutes.length} entries</div>
              <div className="flex items-center gap-2">
                <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm">Page {currentPage} of {totalPages}</span>
                <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'travel' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Travel Allowance</h2>
              <button onClick={() => setShowTravelForm(!showTravelForm)} className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />Add
              </button>
            </div>

            {showTravelForm && (
              <div className="mb-6 p-4 border rounded-lg bg-gray-50">
                <div className="flex gap-4 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-medium mb-2">Description</label>
                    <input type="text" value={newTravel.description} onChange={(e) => setNewTravel({ ...newTravel, description: e.target.value })} placeholder="e.g., Travel 50 - 150" className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium mb-2">Amount Range</label>
                    <input type="text" value={newTravel.amount} onChange={(e) => setNewTravel({ ...newTravel, amount: e.target.value })} placeholder="e.g., 50 - 150" className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                  <button onClick={handleAddTravel} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Add</button>
                  <button onClick={() => setShowTravelForm(false)} className="px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {travelAllowances.map((travel) => (
                <div key={travel.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <p className="text-sm">{travel.description}</p>
                  <div className="flex gap-4">
                    <button className="text-blue-600 hover:text-blue-900"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => handleDeleteTravel(travel.id)} className="text-red-600 hover:text-red-900"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'other' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Other Allowance</h2>
              <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />Add Allowance
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {['Role', 'DA (min/max)', 'EX (min/max)', 'OUT (min/max)'].map(header => (
                      <th key={header} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {otherAllowances.map((allowance, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium">{allowance.role}</td>
                      <td className="px-6 py-4 text-sm text-blue-600">{allowance.da_min} / {allowance.da_max}</td>
                      <td className="px-6 py-4 text-sm text-blue-600">{allowance.ex_min} / {allowance.ex_max}</td>
                      <td className="px-6 py-4 text-sm text-blue-600">{allowance.out_min} / {allowance.out_max}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showRouteForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-2xl font-semibold">{editingRoute ? 'Edit Route' : 'Add New Route'}</h2>
              <button onClick={() => setShowRouteForm(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Route ID *</label>
                  <input type="text" value={routeFormData.route_id} onChange={(e) => setRouteFormData({ ...routeFormData, route_id: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Route Name *</label>
                  <input type="text" value={routeFormData.route_name} onChange={(e) => setRouteFormData({ ...routeFormData, route_name: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Cities *</label>
                  <input type="text" value={routeFormData.cities} onChange={(e) => setRouteFormData({ ...routeFormData, cities: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Zone *</label>
                  <select value={routeFormData.zone} onChange={(e) => setRouteFormData({ ...routeFormData, zone: e.target.value })} className="w-full px-3 py-2 border rounded-lg">
                    <option value="Bihar">Bihar</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Division</label>
                  <input type="text" value={routeFormData.division} onChange={(e) => setRouteFormData({ ...routeFormData, division: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Route For</label>
                  <input type="text" value={routeFormData.route_for} onChange={(e) => setRouteFormData({ ...routeFormData, route_for: e.target.value })} placeholder="Employee name" className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Designation</label>
                  <input type="text" value={routeFormData.designation} onChange={(e) => setRouteFormData({ ...routeFormData, designation: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Mode *</label>
                  <select value={routeFormData.mode} onChange={(e) => setRouteFormData({ ...routeFormData, mode: e.target.value })} className="w-full px-3 py-2 border rounded-lg">
                    <option value="Bike">Bike</option>
                    <option value="Train">Train</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Distance (KM) *</label>
                  <input type="number" value={routeFormData.distance} onChange={(e) => setRouteFormData({ ...routeFormData, distance: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-lg" min="0" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Fare *</label>
                  <input type="number" value={routeFormData.fare} onChange={(e) => setRouteFormData({ ...routeFormData, fare: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-lg" min="0" step="0.01" />
                </div>
              </div>
            </div>

            <div className="p-6 border-t flex justify-end gap-3">
              <button onClick={() => setShowRouteForm(false)} className="px-6 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleSaveRoute} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">{editingRoute ? 'Update' : 'Add'} Route</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllowanceSettings;
